<script setup lang="ts">
    import { reactive, onMounted } from 'vue';
    import { type TextMessage } from '../TextMessagesTypes';
    import { loadTextMessage, abortSending } from '../TextMessages.access';
    import Loading from '@shared/components/Loading.vue';
    import { formatDateTime } from '@cwd/Shared/utils/formatDateTime';
    import NavCreate from '@cwd/Navigation/NavCreate.vue';
    import PopoverSurvey from '@shared/components/PopoverSurvey.vue';
    import Countdown from '@shared/components/Countdown.vue';
    import { bsAlert, AlertTypes } from '@shared/components/bsAlerts';

    const props = defineProps<{
        textMessageId: string;
    }>();

    const state = reactive<{
        textMessage: null | TextMessage;
        isBusy: boolean;
        isScheduled: boolean;
    }>({
        textMessage: null,
        isBusy: true,
        isScheduled: false,
    });

    onMounted(async () => {
        state.textMessage = await loadTextMessage(props.textMessageId);
        state.isBusy = false;
        state.isScheduled = isScheduled(state.textMessage.ScheduledForSending);
    });

    const onAbortSending = (id: string, isBusy: boolean, cb?: () => {}) => {
        if (isBusy) return;
        isBusy = true;

        abortSending(id)
            .then(() => {
                bsAlert({ title: $Resources.AlertSmsAbortedHeader, content: $Resources.AlertSmsAbortedBody });
            })
            .catch(function () {
                bsAlert({ type: AlertTypes.Danger, title: $Resources.ErrorAbortingSmsHeader, content: $Resources.ErrorAbortingSmsBody });
            })
            .finally(() => {
                isBusy = false;
            });
    };

    const afterAbort = () => {
        return (window.location.href = '/Textmessages');
    };

    const isScheduled = (dateVal: string) => {
        const start = new Date(dateVal);
        const now = new Date();

        return start > now;
    };
</script>

<template>
    <div v-if="state.textMessage">
        <section class="hero-bg p-lg-all c-white">
            <div class="max-900 container-fluid">
                <div class="form-group" v-if="state.textMessage.Event">
                    <a class="btn btn-default-transparent btn-sm" :href="'/Events/' + state.textMessage.Event.EventId">
                        <i class="fa fa-chevron-left" aria-hidden="true"></i>
                        {{ $Resources.BackToEventBtnText }}
                    </a>
                </div>

                <h1 class="strong">{{ $Resources.SendingHeading }}</h1>

                <p class="text-lg m-lg-b">
                    {{ $Resources.YourTextMessageLabel }} "
                    <strong>{{ state.textMessage.Subject }}</strong>
                    "
                    <span v-show="isScheduled(state.textMessage.ScheduledForSending)">{{ $Resources.SendingStatusTxtScheduled }}</span>
                    <span v-show="!isScheduled(state.textMessage.ScheduledForSending)">{{ $Resources.SendingStatusTxtSent }}</span>
                </p>

                <Countdown :countdownTo="state.textMessage.ScheduledForSending"></Countdown>

                <div class="form-group" v-show="state.isScheduled">
                    <button
                        type="button"
                        class="btn btn-default btn-sm"
                        v-show="isScheduled(state.textMessage.ScheduledForSending)"
                        @click="onAbortSending(state.textMessage.SmsMessageId, state.isBusy, afterAbort)">
                        {{ $Resources.SendingCancelScheduledButton }}
                    </button>
                </div>

                <div class="form-group" v-show="!state.isScheduled">
                    <a :href="'/Textmessages/' + state.textMessage.SmsMessageId + '/Statistics'" role="button" class="btn btn-cta">
                        {{ $Resources.SendingSmsToStatisticsButton }}
                        <i class="fa fa-chevron-right" aria-hidden="true"></i>
                    </a>
                </div>
            </div>
        </section>

        <section class="m-xl-b">
            <div class="max-900 container-fluid m-lg-t m-sm-b">
                <h2 class="h3 m-lg-b">{{ $Resources.SendingDetailedInfo }}</h2>

                <div class="table-responsive">
                    <table class="table">
                        <tbody>
                            <tr>
                                <td class="text-nowrap text-right autofit">{{ $Resources.SendingLabel }}:</td>
                                <td class="strong">
                                    {{ formatDateTime(state.textMessage.ScheduledForSending) }}
                                </td>
                            </tr>
                            <tr>
                                <td class="text-nowrap text-right">{{ $Resources.CategoryLabel }}:</td>
                                <td>
                                    <strong>{{ state.textMessage.Category.Name }}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-nowrap text-right">{{ $Resources.SendingSenderLabel }}:</td>
                                <td>
                                    <strong>{{ state.textMessage.Sender }}</strong>
                                </td>
                            </tr>
                            <tr>
                                <td class="text-nowrap text-right">{{ $Resources.RecipientsLabel }}:</td>
                                <td class="strong">
                                    <ul class="list-inline list-unstyled">
                                        <li v-for="list in state.textMessage.Lists">
                                            <a :href="'https://app.ungapped.com/Lists/' + list.ListId" rel="noopener" target="_blank">
                                                {{ list.Name }}
                                            </a>
                                        </li>
                                        <li v-for="segment in state.textMessage.Segments">
                                            <a :href="'https://app.ungapped.com/Segments/' + segment.SegmentId" rel="noopener" target="_blank">
                                                {{ segment.Name }}
                                            </a>
                                        </li>
                                    </ul>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <h2 class="h3 m-lg-b">{{ $Resources.CreateNewHeading }}</h2>

                <NavCreate></NavCreate>
            </div>
        </section>
    </div>

    <PopoverSurvey id="b7e59bd0-e37c-40c3-98d2-bafc5d817e91"></PopoverSurvey>

    <Loading :show="state.isBusy" loadingStyle="progress-bar" :fixed="true"></Loading>
</template>

<style scoped lang="less"></style>

<style lang="less"></style>
